import faker from 'faker/locale/en';
import type { BuildingProps } from '@commutifi/models/Buildings';
import type { BuildingWithEmployeesCount } from 'api/modules/buildings/api';
import type { WorksiteProps } from '@commutifi/models/Worksites';
import { fakeAddress, randomIn } from '../../_utils';

/**
 * Generate a fake building (canonical location)
 * This is the base building faker used by other modules
 */
export const fakeBuilding = (base: Partial<BuildingProps> = {}): BuildingProps => {
  const lat = base.location?.lat || parseFloat(faker.address.latitude());
  const lng = base.location?.lng || parseFloat(faker.address.longitude());
  return {
    id: base.id || faker.datatype.uuid(),
    createdAt: faker.date.past().toISOString(),
    updatedAt: faker.date.past().toISOString(),
    name: base.name || faker.commerce.department(),
    ...fakeAddress(base),
    location: {
      lat,
      lng
    }
  };
};

/**
 * Generate a fake worksite reference for building relations
 * This is a minimal worksite object as returned by the buildings endpoint
 */
export const fakeWorksiteRef = (base: Partial<WorksiteProps> = {}): WorksiteProps => ({
  id: base.id || faker.datatype.uuid(),
  createdAt: base.createdAt || faker.date.past().toISOString(),
  updatedAt: base.updatedAt || faker.date.past().toISOString(),
  name: base.name || `${faker.address.city()} ${faker.company.companySuffix()}`,
  enterpriseId: base.enterpriseId || faker.datatype.uuid(),
  defaultTimezone: base.defaultTimezone || 'America/New_York',
  regionDescription: base.regionDescription ?? faker.address.state(),
  inboundTripThreshold: base.inboundTripThreshold ?? 15,
  outboundTripThreshold: base.outboundTripThreshold ?? 15
});

/**
 * Generate a fake building with employeesCount and optional worksite relation
 * Extends the base fakeBuilding with additional fields for list views
 */
export const fakeBuildingWithEmployees = (base: Partial<BuildingWithEmployeesCount & {
  worksite?: WorksiteProps;
}> = {}): BuildingWithEmployeesCount & {
  worksite?: WorksiteProps;
} => {
  const building = fakeBuilding(base);
  return {
    ...building,
    employeesCount: base.employeesCount ?? randomIn({
      min: 0,
      max: 200
    }),
    // Default to undefined for deterministic test data - tests should explicitly set worksite when needed
    worksite: base.worksite
  };
};

/**
 * Generate many fake buildings with employees count for testing lists
 * @param count Number of buildings to generate
 * @param base - Partial building properties to apply to all generated buildings
 */
export const generateManyBuildings = (count: number, base: Partial<BuildingWithEmployeesCount & {
  worksite?: WorksiteProps;
}> = {}): (BuildingWithEmployeesCount & {
  worksite?: WorksiteProps;
})[] => Array.from({
  length: count
}, (_, i) => fakeBuildingWithEmployees({
  ...base,
  name: `Building ${i + 1} - ${faker.commerce.department()}`
}));